import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useApolloClient } from '@apollo/client';
import { gql } from 'graphql-tag';
import { v4 as uuid } from 'uuid';
import { useMeContext } from '~/modules/me';
import resourceSkillsFragment, {
  resourceSkillDetailsFragment
} from '~/modules/resource-management/graphql/resourceSkills.fragment.js';
import { useResourceUserDetailsContext } from '~/modules/resource-management/resourceUserDetails/ResourceUserDetailsContext';
import { RESOURCE_USER_DETAILS_ACTION } from '~/modules/resource-management/resourceUserDetails/hooks/resourceUserDetailsAction';
import { updateUserSkillNudgesCache } from './updateUserSkillNudgesCache';
import useBulkAcceptUserSkillNudge from './useBulkAcceptUserSkillNudge';
import {
  combineResourceSkillDetails,
  mapResourceSkillDetailsToSkillParameters
} from './mapSkills';

const BULK_CREATE_AND_ASSIGN_SKILLS_TO_USER = gql`
  mutation bulkCreateAndAssignSkillsToUser(
    $input: BulkCreateAndAssignSkillsToUserInput!
  ) {
    bulkCreateAndAssignSkillsToUser(input: $input) {
      skills {
        ...ResourceSkillDetailsFragment
      }
      error {
        errors {
          skill {
            skillUri
            name
            category {
              id
              name
            }
          }
          displayText
        }
      }
    }
  }

  ${resourceSkillDetailsFragment}
`;

const BULK_ASSIGN_SKILL_TO_USER = gql`
  mutation bulkAssignSkillToUser($input: BulkAssignSkillToUserInput!) {
    bulkAssignSkillToUser(input: $input) {
      skills {
        ...ResourceSkillDetailsFragment
      }
      error {
        errors {
          skill {
            uri
            displayText
            category {
              uri
              displayText
            }
          }
          displayText
        }
      }
    }
  }

  ${resourceSkillDetailsFragment}
`;

const ACCEPT_USER_NUDGES = gql`
  mutation AcceptUserNudges($userUri: String!, $nudgeIds: [String!]!) {
    acceptUserNudges: acceptUserNudges2(
      userUri: $userUri
      nudgeIds: $nudgeIds
    ) {
      nudgeIds
      errors {
        nudgeId
        error
      }
    }
  }
`;

const buildOptimisticSkillAssignment = ({ skillAssignment, tenantSlug }) => {
  const skillUri =
    skillAssignment.uri ||
    `urn:replicon-tenant:${tenantSlug}:skill:temp-${uuid()}`;

  return {
    ...skillAssignment,
    __typename: 'Skill',
    id: skillUri,
    uri: skillUri,
    skillLevel: {
      ...skillAssignment.skillLevel,
      __typename: 'SkillLevel'
    },
    skillCustomMetadata: skillAssignment.skillCustomMetadata.map(
      customMetadata => ({
        ...customMetadata,
        __typename: 'SkillCustomMetadata'
      })
    ),
    isDelaRecommendation: false
  };
};

const buildOptimisticResourceSkillDetails = ({
  cache,
  userUri,
  tenantSlug,
  acceptedSkills
}) => {
  const cachedFragmentId = `ResourceDetails:${userUri}`;
  const cachedResourceDetails = cache.readFragment({
    id: cachedFragmentId,
    fragment: resourceSkillsFragment,
    fragmentName: 'ResourceSkillsFragment'
  });
  const { resourceSkill: resourceSkills } = cachedResourceDetails;

  const combinedSkills = combineResourceSkillDetails({
    existingResourceSkillDetails: resourceSkills,
    newResourceSkillDetails: acceptedSkills,
    mergeSkills: (_existingSkill, newSkill) => newSkill
  });

  return combinedSkills.map(resourceSkillDetails => ({
    category: {
      ...resourceSkillDetails.category,
      __typename: 'Category',
      id: resourceSkillDetails.category.uri
    },
    skillAssignments: resourceSkillDetails.skillAssignments.map(
      skillAssignment =>
        buildOptimisticSkillAssignment({ skillAssignment, tenantSlug })
    ),
    __typename: 'ResourceSkillDetails'
  }));
};

const updateResourceSkillsCache = ({ cache, skills, userUri }) => {
  const cachedFragmentId = `ResourceDetails:${userUri}`;
  const cachedResourceDetails = cache.readFragment({
    id: cachedFragmentId,
    fragment: resourceSkillsFragment,
    fragmentName: 'ResourceSkillsFragment'
  });

  cache.writeFragment({
    id: cachedFragmentId,
    fragment: resourceSkillsFragment,
    fragmentName: 'ResourceSkillsFragment',
    data: {
      ...cachedResourceDetails,
      resourceSkill: skills
    }
  });
};

const getCreatedAndAssignedSkillsNudgeIds = ({
  acceptedSkills,
  skillAssignmentErrors
}) => {
  if (!skillAssignmentErrors?.length)
    return acceptedSkills.flatMap(x =>
      x.skillAssignments.map(skill => skill.nudgeId)
    );

  const categorySkillMap = skillAssignmentErrors
    .map(error => error.skill)
    .reduce((acc, skill) => {
      if (!acc[skill.category.name.toLowerCase()])
        acc[skill.category.name.toLowerCase()] = {};
      acc[skill.category.name.toLowerCase()][skill.name.toLowerCase()] = true;

      return acc;
    }, {});

  return acceptedSkills
    .reduce((acc, x) => {
      const errorCategory =
        categorySkillMap[x.category.displayText.toLowerCase()];

      if (!errorCategory) {
        acc.push(x.skillAssignments.map(skill => skill.nudgeId));
      } else {
        acc.push(
          x.skillAssignments
            .filter(skill => !errorCategory[skill.displayText.toLowerCase()])
            .map(skill => skill.nudgeId)
        );
      }

      return acc;
    }, [])
    .flat();
};

const useOnAcceptUserNudges = ({ userUri, canCreateSkills }) => {
  const apolloClient = useApolloClient();
  const {
    tenantSlug,
    featureFlags: { isPsaPraaUserProfileNudgesEnabled }
  } = useMeContext();
  const intl = useIntl();
  const { dispatch } = useResourceUserDetailsContext();

  const [bulkCreateAndAssignSkillsToUserMutation] = useMutation(
    BULK_CREATE_AND_ASSIGN_SKILLS_TO_USER,
    {}
  );
  const [bulkAssignSkillToUserMutation] = useMutation(
    BULK_ASSIGN_SKILL_TO_USER,
    {}
  );
  const [acceptUserNudges] = useMutation(ACCEPT_USER_NUDGES, {});
  const { bulkAcceptUserSkillNudge } = useBulkAcceptUserSkillNudge({ userUri });

  const bulkCreateAndAssignSkillsToUser = useCallback(
    async acceptedSkills => {
      const {
        data: { bulkCreateAndAssignSkillsToUser: response }
      } = await bulkCreateAndAssignSkillsToUserMutation({
        variables: {
          input: {
            skills: acceptedSkills.flatMap(
              mapResourceSkillDetailsToSkillParameters
            ),
            userUri
          }
        },
        optimisticResponse: {
          bulkCreateAndAssignSkillsToUser: {
            skills: buildOptimisticResourceSkillDetails({
              cache: apolloClient.cache,
              userUri,
              tenantSlug,
              acceptedSkills
            }),
            error: null,
            __typename: 'BulkCreateAndAssignSkillsToUserResult'
          }
        },
        update: (cache, mutationResponse) =>
          updateResourceSkillsCache({
            cache,
            skills:
              mutationResponse.data.bulkCreateAndAssignSkillsToUser.skills,
            userUri
          })
      });

      return {
        errors: response.error?.errors || []
      };
    },
    [bulkCreateAndAssignSkillsToUserMutation, userUri, tenantSlug, apolloClient]
  );

  const bulkAssignSkillToUser = useCallback(
    async acceptedSkills => {
      const skillParameters = acceptedSkills.flatMap(
        mapResourceSkillDetailsToSkillParameters
      );
      const skillParametersMap = skillParameters.reduce((acc, skillParam) => {
        acc[skillParam.skillUri] = skillParam;

        return acc;
      });

      const {
        data: { bulkAssignSkillToUser: response }
      } = await bulkAssignSkillToUserMutation({
        variables: {
          input: {
            skills: skillParameters.map(skillParam => ({
              skillUri: skillParam.skillUri,
              skillLevelUri: skillParam.skillLevelUri,
              certificate: skillParam.certificate,
              skillCustomMetadata: skillParam.skillCustomMetadata
            })),
            userUri
          }
        },
        optimisticResponse: {
          bulkAssignSkillToUser: {
            skills: buildOptimisticResourceSkillDetails({
              cache: apolloClient.cache,
              userUri,
              tenantSlug,
              acceptedSkills
            }),
            error: null,
            __typename: 'BulkAssignSkillToUserResult'
          }
        },
        update: (cache, mutationResponse) =>
          updateResourceSkillsCache({
            cache,
            skills: mutationResponse.data.bulkAssignSkillToUser.skills,
            userUri
          })
      });

      return {
        errors:
          response.error?.errors.map(error => ({
            skill: skillParametersMap[error.skill.uri],
            displayText: error.displayText
          })) || []
      };
    },
    [bulkAssignSkillToUserMutation, userUri, tenantSlug, apolloClient]
  );

  const onAcceptUserNudgesComplete = useCallback(
    ({ requestedSkills, errorCount }) => {
      if (!isPsaPraaUserProfileNudgesEnabled) return;

      const requestedSkillCount = requestedSkills.reduce(
        (acc, x) => acc + x.skillAssignments.length,
        0
      );

      if (!requestedSkillCount) return;

      dispatch({
        type: RESOURCE_USER_DETAILS_ACTION.SET_SNACKBAR_MESSAGE,
        snackbarMessage: errorCount
          ? intl.formatMessage(
              { id: 'skillInfoCard.suggestionAcceptedError' },
              { count: requestedSkillCount }
            )
          : requestedSkillCount > 1
          ? intl.formatMessage({ id: 'skillInfoCard.allSuggestionsAccepted' })
          : intl.formatMessage(
              { id: 'skillInfoCard.suggestionAccepted' },
              { skill: requestedSkills[0].skillAssignments[0].displayText }
            )
      });
    },
    [dispatch, intl, isPsaPraaUserProfileNudgesEnabled]
  );

  const onAcceptUserNudges = useCallback(
    async ({ suggestions: acceptedSkills }) => {
      const { errors: skillAssignmentErrors } = canCreateSkills
        ? await bulkCreateAndAssignSkillsToUser(acceptedSkills)
        : await bulkAssignSkillToUser(acceptedSkills);

      const nudgeIds = getCreatedAndAssignedSkillsNudgeIds({
        acceptedSkills,
        skillAssignmentErrors
      });

      let bulkAcceptUserSkillNudgeErrors = [];

      if (isPsaPraaUserProfileNudgesEnabled) {
        const {
          data: { bulkAcceptUserSkillNudge: bulkAcceptUserSkillNudgeData }
        } = await bulkAcceptUserSkillNudge(nudgeIds);

        bulkAcceptUserSkillNudgeErrors =
          bulkAcceptUserSkillNudgeData?.errors || [];
      } else {
        const {
          data: { acceptUserNudges: acceptUserNudgesData }
        } = await acceptUserNudges({
          variables: {
            userUri,
            nudgeIds
          },
          update: (cache, mutationResponse) =>
            updateUserSkillNudgesCache({
              cache,
              userUri,
              nudgeIds: mutationResponse.data?.acceptUserNudges?.nudgeIds
            })
        });

        bulkAcceptUserSkillNudgeErrors = acceptUserNudgesData?.errors || [];
      }

      onAcceptUserNudgesComplete({
        requestedSkills: acceptedSkills,
        errorCount:
          bulkAcceptUserSkillNudgeErrors.length +
          (skillAssignmentErrors?.length || 0)
      });
    },
    [
      bulkCreateAndAssignSkillsToUser,
      bulkAssignSkillToUser,
      acceptUserNudges,
      bulkAcceptUserSkillNudge,
      onAcceptUserNudgesComplete,
      userUri,
      canCreateSkills,
      isPsaPraaUserProfileNudgesEnabled
    ]
  );

  return {
    onAcceptUserNudges,
    bulkCreateAndAssignSkillsToUser,
    bulkAssignSkillToUser
  };
};

export default useOnAcceptUserNudges;
